// MAMORU - メインUIロジック (v0.2)
import { Detector } from './lib/detect';
import { judge, applyJudgement, visualSearch } from './lib/api';
import type { DataBundle, DetectResult } from './lib/types';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const promptEl = $<HTMLTextAreaElement>('prompt');
const charCountEl = $('charCount');
const statusEl = $('resultStatus');
const statusTextEl = statusEl.querySelector('.status-text') as HTMLElement;
const statusSubEl = $('statusSub');
const ringIconEl = statusEl.querySelector('.ring-icon') as HTMLElement;
const detailEl = $('resultDetail');
const actionsEl = $('resultActions');
const judgeModeEl = $('judgeMode');
const judgeModeText = judgeModeEl.querySelector('.jm-text') as HTMLElement;
const vectorPanelEl = $('vectorPanel');
const vectorSummaryEl = $('vectorSummary');
const vectorBodyEl = $('vectorBody');
const visualPanelEl = $('visualPanel');
const visualSummaryEl = $('visualSummary');
const visualBodyEl = $('visualBody');
const copyBtn = $<HTMLButtonElement>('copyBtn');
const dataMetaEl = $('dataMeta');
const statChars = $('statChars');
const statWorks = $('statWorks');
const clearBtn = $<HTMLButtonElement>('clearBtn');

let detector: Detector | null = null;
let lastResult: DetectResult | null = null;
let debounceTimer: number | null = null;

async function loadData(): Promise<DataBundle> {
  // Vite の base 設定（'/' or '/mamoru/'）に追従
  const base = import.meta.env.BASE_URL || '/';
  const files = [
    'db.json', 'public_domain.json', 'common_words.json',
    'sites.json', 'work_aliases.json', 'character_aliases.json'
  ];
  const [db, pd, common, sites, workAliases, charAliases] = await Promise.all(
    files.map(f => fetch(`${base}data/${f}`).then(r => {
      if (!r.ok) throw new Error(`Failed to fetch ${f}: ${r.status}`);
      return r.json();
    }))
  );
  return { db, pd, common, sites, workAliases, charAliases };
}

type StatusKind = 'idle' | 'checking' | 'clean' | 'warn' | 'danger';
const STATUS_PRESET: Record<StatusKind, { icon: string; text: string; sub: string }> = {
  idle:     { icon: '⏳', text: '入力待ち', sub: 'プロンプトを貼り付けると自動でチェックします' },
  checking: { icon: '◐', text: '検査中…', sub: '辞書照合と文脈判定を実行しています' },
  clean:    { icon: '✓',  text: 'クリア',  sub: '著作権キャラは検出されませんでした' },
  warn:     { icon: '!',  text: '要注意',  sub: '同名のキャラ・作品名が見つかりました' },
  danger:   { icon: '⚠',  text: '危険',    sub: '著作権キャラと作品名の両方が一致しました' },
};

function setStatus(kind: StatusKind, opts?: { text?: string; sub?: string }) {
  statusEl.className = `result-status status-${kind}`;
  const p = STATUS_PRESET[kind];
  ringIconEl.textContent = p.icon;
  statusTextEl.textContent = opts?.text ?? p.text;
  statusSubEl.textContent = opts?.sub ?? p.sub;
}

type JudgeMode = 'pending' | 'vector' | 'dict' | 'error';
const JUDGE_LABEL: Record<JudgeMode, string> = {
  pending: '待機',
  vector: '✨ ベクトル判定済',
  dict: '🔠 辞書のみ',
  error: '⚠ 判定不能',
};
function setJudgeMode(mode: JudgeMode) {
  judgeModeEl.className = `judge-mode mode-${mode}`;
  judgeModeText.textContent = JUDGE_LABEL[mode];
}

function escapeHTML(s: string): string {
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}

function renderResult(result: DetectResult, vectorChecked: boolean) {
  if (!result || result.hits.length === 0) {
    setStatus('clean', {
      sub: vectorChecked
        ? 'サーバー側ベクトル検索でも問題なしと判定されました'
        : 'このプロンプトはそのまま生成サービスへ送信できます'
    });
    detailEl.innerHTML = '';
    actionsEl.hidden = false;
    return;
  }

  const kind: StatusKind =
    result.level === 'red' ? 'danger' :
    result.level === 'yellow' ? 'warn' :
    result.level === 'green' ? 'clean' : 'idle';

  const subMap: Record<DetectResult['level'], string> = {
    red:    `${result.hits.length}件の著作権キャラを検出（生成・配布前に要確認）`,
    yellow: result.workOnly
      ? '作品名が検出されました（キャラ未指定）'
      : '同名のキャラが存在します・要確認',
    green:  '古典・パブリックドメイン文脈と判定されました',
    none:   'クリア',
  };
  setStatus(kind, { sub: subMap[result.level] });

  const items = result.hits.slice(0, 10).map(h => {
    const works = h.works.slice(0, 4)
      .map(w => `<span class="work-chip">${escapeHTML(w.t)}</span>`).join('');
    return `
      <li class="hit-${h.level}">
        <span class="hit-name">${escapeHTML(h.name)}</span>
        <span class="hit-works">${works}</span>
      </li>
    `;
  }).join('');

  detailEl.innerHTML = `<ul class="hits">${items}</ul>`;
  actionsEl.hidden = false;
}

// 入力テキストが「特徴記述として有効」か判定
// 1単語/短すぎ/形容詞だけ などはベクトル検索に値しない
function isMeaningfulInput(text: string): boolean {
  const t = text.trim();
  if (t.length < 8) return false;
  // 句読点・空白・「、」「。」「・」で区切った要素数を数える
  const tokens = t.split(/[\s、。・,．,／/]+/).filter(s => s.length > 0);
  if (tokens.length < 2) return false;
  return true;
}

// 特徴一致検出パネル（名前なしの描写から想起されるキャラを検出）
//
// フィルタ戦略:
//   ・入力が短すぎ・1語のみ → 表示しない
//   ・確実（Top1≥0.65 かつ 差≥0.08）→ Top1 のみ表示
//   ・上記未満 → 一切表示しない
function renderVisualPanel(matchesRaw: import('./lib/api').VisualMatch[], inputText?: string) {
  if (!matchesRaw || matchesRaw.length === 0) {
    visualPanelEl.hidden = true;
    return;
  }
  // 入力テキスト品質チェック
  if (inputText && !isMeaningfulInput(inputText)) {
    visualPanelEl.hidden = true;
    return;
  }

  // 名前重複除去（curated データに重複登録があるため、同名の上位スコアのみ残す）
  const seen = new Set<string>();
  const matches = matchesRaw.filter(m => {
    if (seen.has(m.name)) return false;
    seen.add(m.name);
    return true;
  });

  if (matches.length === 0) {
    visualPanelEl.hidden = true;
    return;
  }

  const top1 = matches[0];
  const top2 = matches[1];
  const gap = top2 ? top1.score - top2.score : 1.0;

  // しきい値:
  //   strong: Top1≥0.70 かつ Top2との差≥0.10
  //   medium: Top1≥0.62 かつ Top2との差≥0.08（「黄色のネズミ」=0.640/差0.094 を救う）
  let level: 'strong' | 'medium' | null = null;
  if (top1.score >= 0.70 && gap >= 0.10) {
    level = 'strong';
  } else if (top1.score >= 0.62 && gap >= 0.08) {
    level = 'medium';
  }

  if (!level) {
    visualPanelEl.hidden = true;
    return;
  }

  visualPanelEl.hidden = false;
  const summaryLabel = level === 'strong' ? '確実な一致' : '候補';
  visualSummaryEl.textContent = `${summaryLabel} ・ score ${top1.score.toFixed(2)}`;

  const works = (top1.works || []).slice(0, 3)
    .map(w => `<span class="work-chip">${escapeHTML(w)}</span>`).join('');
  const cls = top1.score >= 0.7 ? 'score-high' : 'score-mid';
  const item = `
    <li>
      <span class="vis-name">${escapeHTML(top1.name)}</span>
      <span class="vis-works">${works}</span>
      <span class="vis-score ${cls}">${top1.score.toFixed(2)}</span>
    </li>
  `;

  visualBodyEl.innerHTML = `
    <p style="font-size:12px;color:var(--text-sub);margin:8px 0 4px;">
      入力テキストの特徴描写から、想起される可能性のあるキャラクターです。
    </p>
    <ul class="visual-list">${item}</ul>
  `;
}

// ベクトル分析パネル：判定スコアを別表示
function renderVectorPanel(allWithVector: { name: string; vector?: NonNullable<import('./lib/types').Hit['vector']> }[]) {
  const items = allWithVector.filter(h => h.vector);
  if (items.length === 0) {
    vectorPanelEl.hidden = true;
    return;
  }
  vectorPanelEl.hidden = false;
  const suppressed = items.filter(h => h.vector!.decision === 'suppress').length;
  vectorSummaryEl.textContent = `${items.length}件分析 ${suppressed > 0 ? `・${suppressed}件抑制` : ''}`;

  const decLabel: Record<string, string> = {
    red: '検知', yellow: '注意', green: 'PD', suppress: '抑制'
  };
  const rows = items.map(h => {
    const v = h.vector!;
    const fpct = Math.round(v.fictional * 100);
    const npct = Math.round(v.noise * 100);
    return `
      <tr>
        <td class="vp-name">${escapeHTML(h.name)}</td>
        <td>
          <div class="vp-bar-wrap">
            <div class="vp-bar"><div class="vp-bar-fill fill-fiction" style="width:${fpct}%"></div></div>
            <span class="vp-num">${v.fictional.toFixed(2)}</span>
          </div>
        </td>
        <td>
          <div class="vp-bar-wrap">
            <div class="vp-bar"><div class="vp-bar-fill fill-noise" style="width:${npct}%"></div></div>
            <span class="vp-num">${v.noise.toFixed(2)}</span>
          </div>
        </td>
        <td><span class="vp-decision dec-${v.decision}">${decLabel[v.decision]}</span></td>
      </tr>
    `;
  }).join('');

  vectorBodyEl.innerHTML = `
    <table class="vp-table">
      <thead>
        <tr>
          <th>名前</th>
          <th>フィクション度</th>
          <th>一般語スコア</th>
          <th>判定</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

async function runCheck() {
  const text = promptEl.value;
  if (!text.trim()) {
    setStatus('idle');
    setJudgeMode('pending');
    detailEl.innerHTML = '';
    actionsEl.hidden = true;
    vectorPanelEl.hidden = true;
    visualPanelEl.hidden = true;
    return;
  }
  if (!detector) return;

  setStatus('checking');
  setJudgeMode('pending');
  const result = detector.detect(text);
  lastResult = result;

  // 特徴一致検出（並列で投げる、名前ヒット有無に関わらず実行）
  const visualPromise = visualSearch(text, 5).catch(() => ({ ok: false } as const));

  if (result.hits.length === 0) {
    setJudgeMode('dict');
    renderResult(result, false);
    renderVectorPanel([]);
    // 名前未検出でも特徴一致は出す
    const vRes = await visualPromise;
    renderVisualPanel(vRes.ok ? (vRes.matches ?? []) : [], text);
    return;
  }

  // サーバー判定
  let vectorChecked = false;
  let serverError = false;
  let allWithVector: typeof result.hits = result.hits;
  try {
    const candidates = result.hits.map(h => ({
      name: h.name,
      works: h.works.map(w => w.t),
    }));
    const resp = await judge({ text, candidates });
    if (resp.ok && resp.results) {
      vectorChecked = true;
      const { kept, all } = applyJudgement(result.hits, resp.results);
      result.hits = kept;
      allWithVector = all;
      let topLevel: DetectResult['level'] = 'none';
      if (kept.some(h => h.level === 'red')) topLevel = 'red';
      else if (kept.some(h => h.level === 'yellow')) topLevel = 'yellow';
      else if (kept.some(h => h.level === 'green')) topLevel = 'green';
      result.level = topLevel;
    } else {
      serverError = true;
      console.warn('[MAMORU] judge API returned error:', resp.error);
    }
  } catch (e) {
    serverError = true;
    console.warn('[MAMORU] judge API failed:', e);
  }

  setJudgeMode(vectorChecked ? 'vector' : (serverError ? 'error' : 'dict'));
  renderResult(result, vectorChecked);
  renderVectorPanel(allWithVector);
  // 名前ヒット時も特徴一致を表示（補完情報として）
  const vRes = await visualPromise;
  renderVisualPanel(vRes.ok ? (vRes.matches ?? []) : [], text);
}

function debouncedCheck() {
  if (debounceTimer != null) clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(runCheck, 350);
}

// テーマ切替（ライト/ダーク）
function setupTheme() {
  const KEY = 'mamoru-theme';
  const body = document.body;
  const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
  const stored = localStorage.getItem(KEY);
  const systemPrefersLight = window.matchMedia?.('(prefers-color-scheme: light)').matches;
  const initial = (stored as 'light' | 'dark' | null) || (systemPrefersLight ? 'light' : 'dark');
  body.setAttribute('data-theme', initial);
  if (meta) meta.content = initial === 'light' ? '#f8fafc' : '#0b0e14';

  const btn = document.getElementById('themeToggle');
  btn?.addEventListener('click', () => {
    const cur = body.getAttribute('data-theme');
    const next = cur === 'dark' ? 'light' : 'dark';
    body.setAttribute('data-theme', next);
    localStorage.setItem(KEY, next);
    if (meta) meta.content = next === 'light' ? '#f8fafc' : '#0b0e14';
  });
}

async function init() {
  setupTheme();
  setStatus('checking', { text: 'データ読み込み中…', sub: '辞書を取得しています' });
  try {
    const data = await loadData();
    detector = new Detector(data);
    const { char_count, work_count, search_key_count } = data.db.meta as
      { char_count: number; work_count: number; search_key_count?: number };
    const skc = search_key_count ?? char_count;
    dataMetaEl.textContent = `${char_count.toLocaleString()}キャラ（検索キー${skc.toLocaleString()}）・ ${work_count.toLocaleString()}作品 ロード済`;
    statChars.textContent = char_count.toLocaleString();
    statWorks.textContent = work_count.toLocaleString();
    setStatus('idle');
  } catch (e) {
    setStatus('danger', { text: 'データ読み込み失敗', sub: 'ページを再読み込みしてください' });
    detailEl.innerHTML = `<pre>${escapeHTML(String(e))}</pre>`;
    return;
  }

  promptEl.addEventListener('input', () => {
    charCountEl.textContent = `${promptEl.value.length.toLocaleString()} 文字`;
    debouncedCheck();
  });

  promptEl.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      runCheck();
    }
  });

  copyBtn.addEventListener('click', async () => {
    await navigator.clipboard.writeText(promptEl.value);
    const original = copyBtn.innerHTML;
    copyBtn.innerHTML = '<span>✅ コピー完了</span>';
    setTimeout(() => { copyBtn.innerHTML = original; }, 1400);
  });

  clearBtn.addEventListener('click', () => {
    promptEl.value = '';
    promptEl.dispatchEvent(new Event('input'));
    promptEl.focus();
  });
}

init();
